/**
 * Declared store SKUs (Phase 88, 88a).
 *
 * A "declared" projection row records that a variant's live Shopify SKU is
 * already its print SKU: TH standard SKUs (`TEE-M-NY-TH613D-R4`) are kept as
 * they are instead of being renamed to product sync's own grammar. The row
 * lives in `org_variant_custom_print_skus` with `identity_source = 'declared'`
 * and no `base_sku`.
 *
 * This module is the one shared answer to:
 * - the per-org switch (`DECLARED_SKU_MODES`, permanently two-state, S13);
 * - "is this org a composition org" (`isCompositionOrg`);
 * - "which SKU is this variant's artwork target" (`artworkTargetForVariant`):
 *   a declared identity wins only when it equals the live SKU byte for byte,
 *   otherwise today's `syncedSku` answer is reproduced exactly (D-04), and in
 *   store SKU mode only a declared identity is a target (plus, for a caller
 *   that asks with `includeSettable`, a variant Set up would declare now:
 *   88a.1's set up on upload);
 * - "does this declared SKU print both sides" (`declaredSkuIsDualSided`);
 * - "what identity would this live variant declare" (`resolveDeclaredIdentity`):
 *   the org's code tables cross-checked against `parseSKU` segments. It never
 *   goes through product sync's own grammar parser (D-02, D-04);
 * - "is this stored declared row still right" (`classifyDeclaredRow`), shared
 *   by 88-04 Verify and the 88-06 `--verify`;
 * - "where is this product" (`classifyStoreSkuProduct`, the 88-05 per-product
 *   summary, S16), which counts the declare core's own outcomes
 *   (`DECLARE_OUTCOMES`) so it always agrees with Set up.
 *
 * Everything here is pure: no I/O, no clock, no randomness.
 */
import { parseSKU } from '../sku-parser/sku-parser.js';
import { syncedSku } from './platen-size-rules.js';
import { byoDesignIdsOn, CODE_RE, matchSettingsCode } from './product-sync.js';
// ---------------------------------------------------------------------------
// The per-org switch.
// ---------------------------------------------------------------------------
/**
 * `org_product_sync_settings.declared_sku_mode`. Permanently two-state (S13):
 * a third mode would be a new column or a new decision, never a third value
 * here. The database CHECK holds exactly these values.
 */
export const DECLARED_SKU_MODES = ['off', 'on'];
/** True only for exactly `'on'`. Anything else (a missing row, null, a typo) reads as off. */
export function declaredSkuModeIsOn(value) {
    return value === 'on';
}
/**
 * Phase 86 slice B (86-23, R3-1): a store SKU org that uses its own design
 * IDs. Its OLD products (declared, or ever marked skip platen) keep their
 * store SKUs and are never planned by Product sync; its NEW products are
 * planned by Product sync with platen files and derived SKUs, and the store
 * SKU setup leaves them alone. Both switches must be on: BYO alone is a
 * product sync org as today, the store SKU switch alone keeps 86-19's
 * refusal. The ONE predicate every surface uses.
 */
export function isStoreSkuByoOrg({ settings, declaredMode, }) {
    return byoDesignIdsOn(settings) && declaredSkuModeIsOn(declaredMode);
}
/** A stored value as the switch reads it: `on` only for exactly `'on'`, `off` for anything else. */
export function toDeclaredSkuMode(value) {
    return declaredSkuModeIsOn(value) ? 'on' : 'off';
}
/**
 * BLU-1368: message marker of the PRECONDITION_FAILED `setDeclaredSkuMode`
 * throws when `on` is asked while the org's base SKU intake flag is on (store
 * SKUs and base SKU intake never both on, D-04, D-18). tRPC drops error
 * causes, so the message STARTS with `${STORE_SKUS_INTAKE_ON_MARKER}:` and the
 * Settings store SKU section branches on that prefix.
 */
export const STORE_SKUS_INTAKE_ON_MARKER = 'STORE_SKUS_INTAKE_ON';
// ---------------------------------------------------------------------------
// Composition org.
// ---------------------------------------------------------------------------
/**
 * The one definition of a composition org: its metafield mappings include a
 * `design_id` or a `back_design_id` purpose. Accepts any object with a
 * `has(purpose)` method (a Set of purposes, a Map keyed by purpose, ...).
 */
export function isCompositionOrg(mappings) {
    return mappings.has('design_id') || mappings.has('back_design_id');
}
const NO_TARGET = { sku: null, productTypeCode: null, sizeCode: null, dualSided: false };
/**
 * Whether a store SKU prints both sides: `parseSKU`'s own `isDualPrint` (a
 * type segment with a leading `2`, or a V3 print position of `2`); false for
 * a SKU it cannot read. The ONE dual-sided rule for a declared SKU: the
 * artwork target below, and the held count at switch-on and Set up
 * (`declared-ruled-impact.ts`), both ask it, so the count and the grid agree.
 */
export function declaredSkuIsDualSided(sku) {
    try {
        return parseSKU(sku).isDualPrint;
    }
    catch {
        return false;
    }
}
/**
 * The artwork target for one variant.
 *
 * A declared identity is used only when `declared.sku === nativeSku` byte for
 * byte (no trim, no case folding); a declared SKU that differs returns
 * `changed`, never the declared answer and never a derived fallback. Without a
 * declared identity the answer is exactly what today's callers compute:
 * `syncedSku(variant, options)`, the variant's derived codes, and dual-sided
 * when the SKU is the derived SKU with a leading `2`.
 *
 * `storeSkuMode` (the product is listed in store SKU mode: a composition org
 * with the switch on): only a declared identity is a target, so a variant
 * with no declared row is `none` even when its live SKU reads as a derived
 * one. Set up must declare it first. The Products page (`productArtworkTarget`)
 * and the Product updates worklist both pass it, so neither offers a derived
 * upload target the other hides.
 *
 * `includeSettable` (88a.1, D4, store SKU mode only): a variant with no
 * declared row whose `settable.sku` equals its live SKU byte for byte is a
 * `settable` target, with the core's decided codes and the declared
 * dual-sided rule. A declared row always wins; outside store SKU mode
 * `settable` is ignored.
 *
 * `projectedDerived` (86-24, R3-11, store SKU mode only): a variant with no
 * declared row and a derived projection row is a `derived` target with the
 * projected SKU and codes, whatever its live SKU says, dual-sided by the
 * derived rule (a leading `2`). A declared row wins; it outranks `settable`
 * (product sync set the variant up, so Set up never declares it); outside
 * store SKU mode it is ignored.
 */
export function artworkTargetForVariant(variant, options = {}) {
    const { nativeSku, derived, declared, settable, projectedDerived } = variant;
    if (declared) {
        if (declared.sku === nativeSku) {
            return {
                identity: 'declared',
                sku: declared.sku,
                productTypeCode: declared.productTypeCode,
                sizeCode: declared.sizeCode,
                dualSided: declaredSkuIsDualSided(declared.sku),
            };
        }
        return { identity: 'changed', ...NO_TARGET };
    }
    if (options.storeSkuMode) {
        if (projectedDerived?.sku) {
            return {
                identity: 'derived',
                sku: projectedDerived.sku,
                productTypeCode: projectedDerived.productTypeCode,
                sizeCode: projectedDerived.sizeCode,
                dualSided: projectedDerived.sku.startsWith('2'),
            };
        }
        if (options.includeSettable && settable && settable.sku === nativeSku) {
            return {
                identity: 'settable',
                sku: settable.sku,
                productTypeCode: settable.productTypeCode,
                sizeCode: settable.sizeCode,
                dualSided: declaredSkuIsDualSided(settable.sku),
            };
        }
        return { identity: 'none', ...NO_TARGET };
    }
    const sku = syncedSku({ nativeSku, derived }, { includeDualSided: options.includeDualSided });
    if (sku === null)
        return { identity: 'none', ...NO_TARGET };
    return {
        identity: 'derived',
        sku,
        productTypeCode: derived.productCode ?? null,
        sizeCode: derived.sizeCode ?? null,
        dualSided: sku === `2${derived.sku}`,
    };
}
// ---------------------------------------------------------------------------
// Resolving a declared identity from a live variant.
// ---------------------------------------------------------------------------
/**
 * Every reason `resolveDeclaredIdentity` can refuse a variant, in the order
 * that breaks ties when `classifyStoreSkuProduct` picks a blocking reason.
 */
export const DECLARED_IDENTITY_REJECTIONS = [
    'unmapped_type',
    'unmapped_size',
    'type_mismatch',
    'size_mismatch',
    'no_size',
    'invalid_code_format',
    'custom_sku',
    'empty_sku',
    'unparseable_sku',
];
/**
 * The code shape both the tables and the SKU segments must have to be stored.
 * Shared with the declare CLI's code table proposal, so it proposes only
 * codes this resolver would accept.
 */
export const DECLARED_CODE_RE = /^[A-Z0-9]{1,10}$/;
/** A product type code without its dual-sided leading `2` (`2TEE` -> `TEE`); any other code unchanged. */
export function stripDualPrefix(code) {
    return code.startsWith('2') ? code.slice(1) : code;
}
/**
 * The ONE parse gate for a SKU that could be declared, shared by
 * `resolveDeclaredIdentity` (steps 1-5 below) and the declare core's code
 * table proposal, so the proposal never reads a SKU the resolver refuses.
 * In order: a non-empty string (`empty_sku`), no whitespace anywhere
 * (`unparseable_sku`; never trimmed, it is matched byte for byte), `parseSKU`
 * reads it (`unparseable_sku`), not `CUST` (`custom_sku`), a size segment
 * (`no_size`). The code shape is NOT checked here; each caller checks its own.
 * Pure.
 */
export function parseDeclarableSku(sku) {
    if (typeof sku !== 'string' || sku.length === 0)
        return { ok: false, reason: 'empty_sku' };
    if (/\s/.test(sku))
        return { ok: false, reason: 'unparseable_sku' };
    let parsed;
    try {
        parsed = parseSKU(sku);
    }
    catch {
        return { ok: false, reason: 'unparseable_sku' };
    }
    if (parsed.isCustom)
        return { ok: false, reason: 'custom_sku' };
    if (!parsed.sku.size)
        return { ok: false, reason: 'no_size' };
    const colourCode = CODE_RE.test(parsed.sku.color) ? parsed.sku.color : null;
    return {
        ok: true,
        sku,
        productTypeCode: parsed.sku.productType,
        sizeCode: parsed.sku.size,
        dualSided: parsed.isDualPrint,
        colourCode,
    };
}
/**
 * Resolve the declared identity for one live variant, or name why it can't be
 * declared. Pure.
 *
 * Checks, in order (1-5 are `parseDeclarableSku`):
 *  1. the SKU is a non-empty string (`empty_sku`);
 *  2. the SKU holds no whitespace anywhere (`unparseable_sku`): it is stored
 *     and later matched byte for byte, so it is never trimmed here;
 *  3. `parseSKU` reads it (`unparseable_sku`);
 *  4. it is not a `CUST` SKU (`custom_sku`);
 *  5. it has a size segment (`no_size`);
 *  6. the product type maps through the org's table (`unmapped_type`);
 *  7. the size option maps through the org's table (`unmapped_size`);
 *  8. the SKU's type and size segments and both table codes are 1-10
 *     uppercase letters or digits (`invalid_code_format`);
 *  9. the table type equals the SKU type once a leading dual `2` is stripped
 *     from both (`type_mismatch`);
 * 10. the table size equals the SKU size (`size_mismatch`).
 *
 * Table lookups use `matchSettingsCode` (case-insensitive, trim-tolerant on
 * the Shopify names), exactly as product sync does. The stored codes are the
 * SKU's own segments, so a dual SKU stores `2TEE` whether the table says
 * `TEE` or `2TEE`.
 */
export function resolveDeclaredIdentity(input) {
    const { productTypeName, sizeName, settings } = input;
    const parsed = parseDeclarableSku(input.sku);
    if (!parsed.ok)
        return parsed;
    const { productTypeCode: skuType, sizeCode: skuSize } = parsed;
    const tableType = typeof productTypeName === 'string' ? matchSettingsCode(settings.productTypes, productTypeName) : null;
    if (tableType === null)
        return { ok: false, reason: 'unmapped_type' };
    const tableSize = typeof sizeName === 'string' ? matchSettingsCode(settings.sizes, sizeName) : null;
    if (tableSize === null)
        return { ok: false, reason: 'unmapped_size' };
    if (![skuType, skuSize, tableType, tableSize].every((code) => DECLARED_CODE_RE.test(code))) {
        return { ok: false, reason: 'invalid_code_format' };
    }
    if (stripDualPrefix(tableType) !== stripDualPrefix(skuType))
        return { ok: false, reason: 'type_mismatch' };
    if (tableSize !== skuSize)
        return { ok: false, reason: 'size_mismatch' };
    return { ok: true, sku: parsed.sku, productTypeCode: skuType, sizeCode: skuSize, dualSided: parsed.dualSided };
}
/**
 * Is a stored declared row still right? The one check 88-04 Verify and the
 * 88-06 `--verify` share. `live` is the live variant (`{ sku }`) or null when
 * the variant no longer exists.
 */
export function classifyDeclaredRow(row, live) {
    if (row.identitySource !== 'declared')
        return 'not_declared';
    // The CHECK makes this impossible; it is reported, never assumed.
    if (row.hasBaseSku)
        return 'declared_with_base_sku';
    if (live === null)
        return 'variant_missing';
    if (live.sku !== row.customPrintSku)
        return 'changed';
    return 'ok';
}
/**
 * Every outcome the declare core (`resolveDeclaredVariants`, backend
 * `declare-core.ts`) can give one variant. It lives here so the per-product
 * summary below reads the core's own decisions instead of re-deriving them.
 * - `declare`: no row yet, and the SKU resolves; Set up inserts it.
 * - `already_declared_same`: a declared row holds this exact SKU; no write.
 * - `declared_sku_changed`: a declared row holds a different SKU (a store
 *   rename); report only, the row is never rewritten in 88a.
 * - `existing_derived_row`: product sync or the CUST sweep owns this variant.
 * - `pap_product`, `excluded`, `not_active`: the product fails the POD
 *   eligibility rule.
 * - `conflicting_duplicate`: the SKU ends declared on more than one variant
 *   with different codes.
 * - `left_for_product_sync` (86-23, R3-1): a store SKU org in BYO mode, and
 *   the product is NEW (no declared row, never marked skip platen). Product
 *   sync makes its derived SKUs; Set up never declares it. Not a refusal.
 * - the rest: why `resolveDeclaredIdentity` refused the variant.
 */
export const DECLARE_OUTCOMES = [
    'declare',
    'already_declared_same',
    'declared_sku_changed',
    'existing_derived_row',
    'pap_product',
    'excluded',
    'not_active',
    'conflicting_duplicate',
    'left_for_product_sync',
    ...DECLARED_IDENTITY_REJECTIONS,
];
/**
 * Every outcome that refuses a variant Set up has not already handled, in the
 * order that breaks ties for `blockingReason`: the resolver's reasons, then
 * duplicates, then the row and eligibility refusals.
 */
export const STORE_SKU_BLOCKING_REASONS = [
    ...DECLARED_IDENTITY_REJECTIONS,
    'conflicting_duplicate',
    'existing_derived_row',
    'pap_product',
    'excluded',
    'not_active',
];
const _blockingReasonsCoverEveryRefusal = Object.fromEntries(STORE_SKU_BLOCKING_REASONS.map((r) => [r, r]));
void _blockingReasonsCoverEveryRefusal;
const BLOCKING_REASON_SET = new Set(STORE_SKU_BLOCKING_REASONS);
/**
 * Where one product stands (S16), from the declare core's own per-variant
 * outcomes, so the summary can never disagree with what Set up does. The
 * duplicate rule, the eligibility rule and existing rows are the core's; this
 * only counts.
 *
 * A variant with a declared row is judged by that row: set up when its SKU is
 * the live SKU, changed when it is not. Every other variant is `declare`
 * (Set up can add it) or a refusal.
 *
 * Priority: `changed` when any declared row's SKU differs from its live SKU;
 * else `needed` when Set up would declare at least one variant
 * (`settable > 0`); else `partial` when any variant without a declared row is
 * refused; else `new` (86-23) when the product has variants, none declared,
 * and every one is `left_for_product_sync`; else `set_up`.
 *
 * KEEP-IN-LOCKSTEP: `shopify_catalogue_page` (migration 20261103100000) ports this in SQL.
 */
export function classifyStoreSkuProduct(variants) {
    const reasonCounts = new Map();
    let setUp = 0;
    let settable = 0;
    let changed = false;
    let refused = false;
    let left = 0;
    for (const v of variants) {
        if (v.declared) {
            if (v.declared.sku === v.nativeSku)
                setUp += 1;
            else
                changed = true;
            continue;
        }
        if (v.outcome === 'declare') {
            settable += 1;
            continue;
        }
        if (v.outcome === 'left_for_product_sync') {
            left += 1;
            continue;
        }
        if (BLOCKING_REASON_SET.has(v.outcome)) {
            const reason = v.outcome;
            reasonCounts.set(reason, (reasonCounts.get(reason) ?? 0) + 1);
            refused = true;
        }
    }
    let blockingReason = null;
    let best = 0;
    for (const reason of STORE_SKU_BLOCKING_REASONS) {
        const count = reasonCounts.get(reason) ?? 0;
        if (count > best) {
            best = count;
            blockingReason = reason;
        }
    }
    const isNew = variants.length > 0 && left === variants.length;
    const status = changed
        ? 'changed'
        : settable > 0
            ? 'needed'
            : refused
                ? 'partial'
                : isNew
                    ? 'new'
                    : 'set_up';
    return { status, setUp, total: variants.length, settable, blockingReason };
}
